import { FC, useMemo, useState } from 'react';
import ProfileThinIcon from '@/assets/profile-thin.svg';
import { ResidentRow } from '@/components/residents/resident-row';
import { ResidentSearchCombobox } from '@/components/residents/resident-search-combobox';
import { Card } from '@/components/ui/card';
import { CardLabel } from '@/components/ui/card-label';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { SectionContainerLayout } from '@/layouts/section-container-layout';
import { api } from '@/redux/api';
import { Resident } from '@/types/resident';
import { NewUnitWizardFooter } from '../new-unit-wizard-footer';

export type AssociateResidentsStepProps = {
  selectedResidents: Resident[];
  onResidentSelect: (resident: Resident) => void;
  onResidentRemove: (resident: Resident) => void;
};

const queryDebounceMs = 500;

export const AssociateResidentsStep: FC<AssociateResidentsStepProps> = ({
  selectedResidents,
  onResidentSelect,
  onResidentRemove,
}) => {
  const [query, setQuery] = useState('');
  const { data: residents, isFetching: isFetchingResidents } =
    api.useGetResidentsQuery({ query: query.trim() });

  const comboboxOptions = useMemo(() => residents ?? [], [residents]);

  return (
    <>
      <SectionContainerLayout horizontal className="m-0 border-none p-0">
        <SectionHeadingGroup
          className="max-w-96"
          subtitle={`Residents are profiles taken from your ARC provider. If you don't have an ARC connection in CareSurround yet, you should skip this step.`}
          title="Associate resident(s)"
        />
        <Card className="flex min-h-[25rem] min-w-[66.67%] flex-col">
          <ResidentSearchCombobox
            displayExtractor={(option) => option.name}
            identifierExtractor={(option) => option.identifier}
            isLoading={isFetchingResidents}
            keyExtractor={(option) => option.residentId}
            onQueryChange={setQuery}
            onSelect={onResidentSelect}
            options={comboboxOptions}
            placeholder="Search by resident name or profile ID..."
            query={query}
            queryDebounceMs={queryDebounceMs}
            queryExtractor={(option) => option.name}
            selectedOptions={selectedResidents}
          />
          <CardLabel>Resident(s) to be associated:</CardLabel>
          <div className="flex flex-col border-t border-gray-200">
            {selectedResidents.length > 0 ? (
              selectedResidents?.map((resident, index) => (
                <ResidentRow
                  actionText="Remove"
                  key={index}
                  onActionClick={onResidentRemove}
                  resident={resident}
                />
              ))
            ) : (
              <div className="flex flex-col items-center">
                <ProfileThinIcon className="mt-4 h-14 min-h-14 w-14 min-w-14 text-gray-500" />
                <CardLabel className="mb-1 mt-2">
                  No residents selected
                </CardLabel>
                <div className="text-sm text-gray-500">
                  Search for a resident to be associated with this unit
                </div>
              </div>
            )}
          </div>
        </Card>
      </SectionContainerLayout>
      <NewUnitWizardFooter skipable />
    </>
  );
};
